import type {
  OrgModelEntity,
  OrgModelProjection,
} from '../models/OrgModelProjection';

export interface PeopleChartDepartment {
  readonly key: string;
  readonly label: string;
}

export function listPeopleChartDepartments(
  model: OrgModelProjection,
): PeopleChartDepartment[] {
  const membership = departmentMembership(model, positionByPersonId(model));
  return Array.from(new Map(
    Array.from(membership.values()).map((department) => [department.key, department]),
  ).values()).sort((left, right) => left.label.localeCompare(right.label));
}

/** Viewer-permissioned department lenses plus visible headcount. */
export function listPeopleChartDepartmentsWithCounts(
  model: OrgModelProjection,
): Array<PeopleChartDepartment & { count: number }> {
  const membership = departmentMembership(model, positionByPersonId(model));
  const counts = new Map<string, PeopleChartDepartment & { count: number }>();
  for (const department of membership.values()) {
    const existing = counts.get(department.key);
    if (existing) existing.count += 1;
    else counts.set(department.key, { ...department, count: 1 });
  }
  return Array.from(counts.values()).sort((left, right) => (
    right.count - left.count || left.label.localeCompare(right.label)
  ));
}

/** Maps visible people to departments without inferring redacted occupancy. */
export function departmentMembership(
  model: OrgModelProjection,
  positionsByPersonId: ReadonlyMap<string, OrgModelEntity>,
): Map<string, PeopleChartDepartment> {
  const departmentsByAlias = new Map<string, OrgModelEntity>();
  for (const department of model.departments) {
    departmentsByAlias.set(department.id, department);
    if (department.externalId) departmentsByAlias.set(department.externalId, department);
  }
  const departmentBySubject = new Map<string, PeopleChartDepartment>();
  for (const relation of model.relationships) {
    if (relation.predicate !== 'belongs_to') continue;
    const department = departmentsByAlias.get(relation.objectRef);
    if (!department) continue;
    departmentBySubject.set(relation.subjectRef, {
      key: department.id,
      label: department.label,
    });
  }

  const result = new Map<string, PeopleChartDepartment>();
  for (const person of model.people) {
    const position = positionsByPersonId.get(person.id);
    const department = departmentBySubject.get(person.id)
      ?? (position ? departmentBySubject.get(position.id) : undefined)
      ?? departmentFromProps(position?.props, departmentsByAlias)
      ?? departmentFromProps(person.props, departmentsByAlias);
    if (department) result.set(person.id, department);
  }
  return result;
}

function positionByPersonId(
  model: OrgModelProjection,
): ReadonlyMap<string, OrgModelEntity> {
  const result = new Map<string, OrgModelEntity>();
  if (model.occupancyVisibility !== 'visible') return result;
  const peopleIds = new Set(model.people.map((person) => person.id));
  const positionsById = new Map(model.positions.map((position) => [position.id, position]));
  for (const state of model.positionState) {
    const position = positionsById.get(state.positionId);
    if (!position) continue;
    for (const personId of state.occupantPersonIds) {
      if (peopleIds.has(personId) && !result.has(personId)) {
        result.set(personId, position);
      }
    }
  }
  return result;
}

function departmentFromProps(
  props: Readonly<Record<string, unknown>> | undefined,
  departmentsByAlias: ReadonlyMap<string, OrgModelEntity>,
): PeopleChartDepartment | undefined {
  if (!props) return undefined;
  const raw = [
    props.departmentId,
    props.departmentRef,
    props.department,
    props.departmentName,
    props.orgUnit,
  ].map(stringValue).find(Boolean);
  if (!raw) return undefined;
  const entity = departmentsByAlias.get(raw);
  if (entity) return { key: entity.id, label: entity.label };
  return { key: `label:${normalizeKey(raw)}`, label: raw };
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function normalizeKey(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
